import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Badge from '../../../../components/common/Badge/Badge';
import Button from '../../../../components/common/Button/Button';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import FormTextarea from '../../../../components/common/FormTextarea/FormTextarea';
import ViewLinkButton from '../../../../components/common/ViewLinkButton/ViewLinkButton';
import { StudentService } from '../../../../services';
import type { SensitivePeriodState } from '../../../../types/sensitiveStudent';
import { formatDate } from '../../../../utils';
import { getBrazilCivilDate } from '../../../../utils/competence';
import layoutStyles from '../StudentRegister.module.css';
import styles from './SensitiveStudentSection.module.css';

interface SensitiveStudentSectionProps {
  studentId: number;
  readOnly?: boolean;
  /** When true, omits the page title (e.g. inside Histórico accordion). */
  embedded?: boolean;
  onChanged?: () => void;
}

type PendingAction = 'start' | 'update' | 'end';

const emptyState = (): SensitivePeriodState => ({
  current: null,
  history: [],
});

function startedOnLabel(startedAt: string): string | null {
  const parsed = new Date(startedAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return formatDate(getBrazilCivilDate(parsed));
}

const SensitiveStudentSection: React.FC<SensitiveStudentSectionProps> = ({
  studentId,
  readOnly = false,
  embedded = false,
  onChanged,
}) => {
  const [state, setState] = useState<SensitivePeriodState>(emptyState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState('');
  const [endJustification, setEndJustification] = useState('');
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const loadPeriod = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const nextState = await StudentService.getSensitivePeriod(studentId);
      setState(nextState);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Não foi possível carregar aluna sensível.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [studentId]);

  useEffect(() => {
    setPendingAction(null);
    setReason('');
    setEndJustification('');
    void loadPeriod();
  }, [loadPeriod]);

  const hasOpenPeriod = Boolean(state.current);
  const formValid = Boolean(reason.trim());

  const canConfirmForm = useMemo(() => {
    if (readOnly || saving || !formValid) {
      return false;
    }

    if (pendingAction === 'start') {
      return true;
    }

    if (pendingAction === 'update' && state.current) {
      return reason.trim() !== state.current.reason;
    }

    return false;
  }, [formValid, pendingAction, readOnly, reason, saving, state.current]);

  const cancelPending = () => {
    if (saving) {
      return;
    }

    setPendingAction(null);
    setReason('');
    setEndJustification('');
    setError(null);
  };

  const openStartModal = () => {
    if (readOnly || saving || hasOpenPeriod) {
      return;
    }

    setReason('');
    setError(null);
    setPendingAction('start');
  };

  const openUpdateModal = () => {
    if (readOnly || saving || !state.current) {
      return;
    }

    setReason(state.current.reason);
    setError(null);
    setPendingAction('update');
  };

  const openEndConfirm = () => {
    if (readOnly || saving || !hasOpenPeriod) {
      return;
    }

    setEndJustification('');
    setError(null);
    setPendingAction('end');
  };

  const handleConfirm = async () => {
    if (!pendingAction) {
      return;
    }

    if ((pendingAction === 'start' || pendingAction === 'update') && !formValid) {
      setError('Informe o motivo da sensibilidade.');
      return;
    }

    if (pendingAction === 'end' && !endJustification.trim()) {
      setError('Informe a justificativa para deixar de ser aluna sensível.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (pendingAction === 'start') {
        await StudentService.startSensitivePeriod(studentId, reason.trim());
      } else if (pendingAction === 'update') {
        await StudentService.updateSensitivePeriod(studentId, reason.trim());
      } else {
        await StudentService.endSensitivePeriod(studentId, endJustification.trim());
      }

      setPendingAction(null);
      setReason('');
      setEndJustification('');
      await loadPeriod(true);
      onChanged?.();
    } catch (saveError) {
      setError(saveError instanceof Error
        ? saveError.message
        : 'Não foi possível atualizar aluna sensível.');
    } finally {
      setSaving(false);
    }
  };

  const startedOn = state.current ? startedOnLabel(state.current.startedAt) : null;
  const formModalOpen = pendingAction === 'start' || pendingAction === 'update';

  const sectionClassName = [
    styles.section,
    embedded ? styles.embedded : '',
  ].filter(Boolean).join(' ');

  return (
    <section className={sectionClassName}>
      <div className={styles.header}>
        {embedded ? (
          <span className={styles.embeddedLabel}>Situação atual</span>
        ) : (
          <h2 className={layoutStyles.columnTitle}>Aluna sensível</h2>
        )}
        {state.current ? (
          <Badge variant="warning">Sensível</Badge>
        ) : null}
      </div>

      {loading ? (
        <p className={styles.status}>Carregando aluna sensível...</p>
      ) : (
        <>
          {!hasOpenPeriod && !error && (
            <p className={styles.summary}>Aluna ainda não marcada como sensível.</p>
          )}

          {state.current && (
            <div className={styles.currentSummary}>
              <p className={styles.summary}>
                Sensível
                {startedOn ? ` desde ${startedOn}` : ''}
                {' '}
                até desmarcar.
              </p>
              <p className={styles.reasonText}>{state.current.reason}</p>
            </div>
          )}

          {!readOnly && (
            <div className={styles.actions}>
              {hasOpenPeriod ? (
                <>
                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    disabled={saving}
                    onClick={openUpdateModal}
                  >
                    Editar motivo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="small"
                    disabled={saving}
                    onClick={openEndConfirm}
                  >
                    Desmarcar
                  </Button>
                </>
              ) : (
                <ViewLinkButton
                  label="Marcar como sensível"
                  icon={null}
                  disabled={saving}
                  onClick={openStartModal}
                />
              )}
            </div>
          )}

          {error && !pendingAction && <p className={styles.error}>{error}</p>}
        </>
      )}

      <ConfirmModal
        open={formModalOpen}
        title={pendingAction === 'update' ? 'Editar motivo' : 'Marcar aluna sensível'}
        variant="info"
        alignment="left"
        width={520}
        loading={saving}
        confirmLabel={pendingAction === 'update' ? 'Salvar' : 'Marcar como sensível'}
        cancelLabel="Cancelar"
        onCancel={cancelPending}
        onConfirm={() => {
          if (!formValid) {
            setError('Informe o motivo da sensibilidade.');
            return;
          }
          if (pendingAction === 'update' && !canConfirmForm) {
            setError('Altere o motivo para salvar.');
            return;
          }
          void handleConfirm();
        }}
      >
        <div className={styles.formModal}>
          <p className={styles.summary}>
            Informe o motivo da sensibilidade. A aluna permanece sensível até ser desmarcada.
          </p>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`sensitive-reason-modal-${studentId}`}>
              Motivo
            </label>
            <FormTextarea
              id={`sensitive-reason-modal-${studentId}`}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                if (error) setError(null);
              }}
              rows={3}
              disabled={saving}
              placeholder="Informe o motivo da sensibilidade"
              autoFocus
            />
          </div>
          {error && formModalOpen && <p className={styles.error}>{error}</p>}
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={pendingAction === 'end'}
        title="Desmarcar aluna sensível"
        variant="warning"
        alignment="left"
        width={520}
        loading={saving}
        confirmLabel="Desmarcar"
        cancelLabel="Cancelar"
        onCancel={cancelPending}
        onConfirm={() => {
          void handleConfirm();
        }}
      >
        <div className={styles.formModal}>
          <p className={styles.summary}>
            Informe a justificativa por escrito para deixar de ser aluna sensível.
          </p>
          <FormTextarea
            value={endJustification}
            onChange={(event) => {
              setEndJustification(event.target.value);
              if (error) setError(null);
            }}
            rows={4}
            disabled={saving}
            placeholder="Justificativa"
            autoFocus
          />
          {error && pendingAction === 'end' && <p className={styles.error}>{error}</p>}
        </div>
      </ConfirmModal>
    </section>
  );
};

export default SensitiveStudentSection;
